import { validationPopup, showPopup } from "../redux/actions";
import { getSignupRequest } from "../services/UserService";
import { signup as pbSignup } from "./authApi";
import { pocketBaseErrorMessage } from "./errors";
import {
  emailFromSignupRequestResponse,
  normalizeEmailForCompare,
} from "./signupInvite";
import { validateSignupForm } from "./validation";
import { getSignupRequestBaseUrl } from "../config";

function normalizeEmailInput(value) {
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  return trimmed ? trimmed.toLowerCase() : "";
}

function pocketBaseFieldErrors(err) {
  const out = {};
  const data = err?.data || err?.response?.data || err?.response;
  const fields = data?.data;
  if (!fields || typeof fields !== "object") return out;

  Object.entries(fields).forEach(([field, info]) => {
    const msg =
      (typeof info === "object" && info?.message) ||
      (typeof info === "string" ? info : "");
    if (msg) out[field] = msg;
  });

  // Make common PocketBase uniqueness errors user-friendly.
  const emailInfo = fields?.email;
  const emailCode = typeof emailInfo === "object" ? emailInfo?.code : undefined;
  const emailMsg = typeof emailInfo === "object" ? emailInfo?.message : undefined;
  if (
    emailCode === "validation_not_unique" ||
    (typeof emailMsg === "string" && emailMsg.toLowerCase().includes("unique"))
  ) {
    out.email = "Email already in use";
  }

  const usernameInfo = fields?.username;
  const usernameCode =
    typeof usernameInfo === "object" ? usernameInfo?.code : undefined;
  const usernameMsg =
    typeof usernameInfo === "object" ? usernameInfo?.message : undefined;
  if (
    usernameCode === "validation_not_unique" ||
    (typeof usernameMsg === "string" && usernameMsg.toLowerCase().includes("unique"))
  ) {
    out.username = "Username already in use";
  }

  return out;
}

export async function signupSubmit(
  dispatch,
  formData,
  lang = "en",
  visibleInputs
) {
  const data = { ...formData };
  if (data.email != null) data.email = normalizeEmailInput(data.email);
  const errors = validateSignupForm(data, lang, visibleInputs);
  if (Object.keys(errors).length > 0) {
    const errorMessage =
      Object.keys(errors).length > 1
        ? lang === "en"
          ? "Please complete all necessary fields."
          : "必須項目を入力してください。"
        : Object.values(errors)[0];

    dispatch(
      validationPopup({
        message: errorMessage,
        icon: "horror",
        type: "error",
      })
    );
    return errors;
  }

  dispatch(
    validationPopup({
      message: "Loading...",
      icon: "loading",
      type: "loading",
    })
  );

  const optionalFields = [
    "personalBlog",
    "personalSite",
    "facebook",
    "dribbble",
    "behance",
    "instagram",
    "linkedin",
    "figma",
    "threads",
    "x",
    "youtube",
    "tiktok",
  ];

  optionalFields.forEach((field) => {
    data[field] = data[field] || "";
  });

  if (!data.signup_request_id && !(data.signupRid && data.signupToken)) {
    dispatch(
      validationPopup({
        message:
          lang === "en"
            ? "Open signup from the link in your confirmation email, or use an invitation link."
            : "確認メールのリンク、または招待リンクからサインアップを開いてください。",
        icon: "horror",
        type: "error",
      })
    );
    return { form: "Missing signup authorization" };
  }

  if (data.signup_request_id) {
    try {
      const inviteRes = await getSignupRequest(data.signup_request_id);
      const inviteEmail = emailFromSignupRequestResponse(inviteRes);
      const a = normalizeEmailForCompare(inviteEmail);
      const b = normalizeEmailForCompare(data.email);
      if (!a || a !== b) {
        dispatch(
          validationPopup({
            message:
              lang === "en"
                ? "This invitation is for a different email address. Use the email from your invite."
                : "この招待は別のメールアドレス用です。招待メールの宛先をご利用ください。",
            icon: "horror",
            type: "error",
          })
        );
        return { email: "Invitation email mismatch" };
      }
    } catch (err) {
      console.error("signup invite verification:", err);
      dispatch(
        validationPopup({
          message:
            pocketBaseErrorMessage(err) ||
            (lang === "en"
              ? "Invalid or expired invitation link."
              : "招待リンクが無効か期限切れです。"),
          icon: "horror",
          type: "error",
        })
      );
      return { signup_request_id: "Invalid or expired invitation" };
    }
  }

  try {
    // If user came from the 15-min confirm-link flow, validate it before creating the user.
    if (data.signupRid && data.signupToken) {
      const base = getSignupRequestBaseUrl();
      try {
        const res = await fetch(`${base}/signup-request/status`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ rid: data.signupRid, token: data.signupToken }),
        });
        const json = await res.json().catch(() => null);
        const ok = Boolean(json?.ok);
        const expired = Boolean(json?.expired);
        const status = typeof json?.status === "string" ? json.status : "";
        if (!res.ok || !ok || expired || (status && status !== "verified")) {
          dispatch(
            validationPopup({
              message:
                lang === "en"
                  ? "Signup link expired. Please request a new link."
                  : "リンクの有効期限が切れました。もう一度お試しください。",
              icon: "horror",
              type: "error",
            })
          );
          return { email: "Link expired" };
        }
      } catch {
        dispatch(
          validationPopup({
            message:
              lang === "en"
                ? "Could not validate signup link. Please try again."
                : "確認に失敗しました。もう一度お試しください。",
            icon: "horror",
            type: "error",
          })
        );
        return { email: "Link validation failed" };
      }
    }

    const mediaLinks = [];
    [
      "facebook",
      "dribbble",
      "behance",
      "instagram",
      "linkedin",
      "figma",
      "threads",
      "x",
      "youtube",
      "tiktok",
    ].forEach((key) => {
      if (data[key]) mediaLinks.push({ type: key, link: data[key] });
    });

    const pbData = {
      email: data.email,
      password: data.password,
      username: data.username,
      fullName: data.fullName,
      nationality: data.nationality,
      intro: data.intro,
      sub_role: data.subRole,
      experience: data.experience,
      personalSite: data.personalSite,
      personalBlog: data.personalBlog,
      mediaLinks,
      isPublic: true,
    };

    const created = await pbSignup(pbData, data.image);

    if (created && created.id) {
      // Best-effort cleanup of the signup request after successful account creation.
      if (data.signupRid && data.signupToken) {
        const base = getSignupRequestBaseUrl();
        fetch(`${base}/signup-request/cancel`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ rid: data.signupRid, token: data.signupToken }),
        }).catch(() => {});
      }
      dispatch(validationPopup(false));
      dispatch(showPopup("congrats"));
    } else {
      dispatch(
        validationPopup({
          message: "Signup failed",
          icon: "horror",
          type: "error",
        })
      );
    }
  } catch (error) {
    console.error("Error during submission:", error);
    const fieldErrors = pocketBaseFieldErrors(error);
    const message =
      Object.keys(fieldErrors).length > 0
        ? lang === "en"
          ? "Please fix the highlighted fields."
          : "ハイライトされた項目を修正してください。"
        : pocketBaseErrorMessage(error);
    dispatch(
      validationPopup({
        message,
        icon: "horror",
        type: "error",
      })
    );
    if (Object.keys(fieldErrors).length > 0) return fieldErrors;
    return { form: pocketBaseErrorMessage(error) };
  }
}
